import { SESSION_END_REASON, SESSION_STATUS } from '../constants';

/**
 * The session as a state machine: every way a session starts, renews and ends,
 * in one pure function.
 *
 * `transition(state, event)` returns the next state and the effects to run.
 * It never touches storage, the network or the clock — `sessionStore` reads
 * those, feeds them in as events, and carries the effects out. So the whole
 * lifecycle can be read, and tested, as a table.
 *
 * Effects:
 * - `persist`    store `refreshToken` (and the build key) for the next reload
 * - `clear`      forget the stored refresh token
 * - `refresh`    exchange `refreshToken` for an access token
 * - `revoke`     ask the server to delete `refreshToken`; the outcome is ignored
 * - `ended`      the session is over: run the app's teardown with `reason`
 *
 * Invariants the table below keeps:
 * 1. Every way into `signedOut` from a session emits exactly one `ended`.
 * 2. Only the refresh endpoint refusing the token (`REFRESH_REJECTED`) ends a
 *    session for being invalid. A network error or 5xx never does.
 * 3. Logging out ends the session locally whatever the server says: `revoke`
 *    is fire-and-forget.
 * 4. A refresh that lands after the session ended is ignored, so a late answer
 *    cannot sign anyone back in.
 */

const { BOOTING, SIGNED_IN, SIGNED_OUT, UNREACHABLE } = SESSION_STATUS;

const EMPTY = Object.freeze({
  accessToken: null,
  accessTokenExpiresAt: null,
  refreshToken: null,
});

/** Before `BOOT`: nothing has been read from storage yet. */
export const INITIAL_SESSION = Object.freeze({
  status: BOOTING,
  ...EMPTY,
  endReason: null,
  started: false,
});

const signedOut = (state, reason) => ({
  ...state,
  ...EMPTY,
  status: SIGNED_OUT,
  endReason: reason,
});

const signedIn = (state, tokens) => ({
  ...state,
  status: SIGNED_IN,
  accessToken: tokens.accessToken,
  accessTokenExpiresAt: tokens.accessTokenExpiresAt,
  refreshToken: tokens.refreshToken,
  endReason: null,
});

const booting = (state, refreshToken) => ({
  ...state,
  ...EMPTY,
  status: BOOTING,
  refreshToken,
  endReason: null,
});

const hasSession = (state) => state.status !== SIGNED_OUT;

const end = (state, reason, effects) => ({
  state: signedOut(state, reason),
  effects: [...effects, { type: 'ended', reason }],
});

const same = (state) => ({ state, effects: [] });

const HANDLERS = {
  /**
   * The app started. `storedRefreshToken` and `storedBuildKey` are what
   * localStorage held; `buildKey` is this build's.
   */
  BOOT(state, { storedRefreshToken, storedBuildKey, buildKey }) {
    if (state.started) return same(state);
    const started = { ...state, started: true };

    if (!storedRefreshToken) {
      return {
        state: signedOut(started, SESSION_END_REASON.NONE),
        // An empty string is what the old logout wrote; tidy it away.
        effects: storedRefreshToken === '' ? [{ type: 'clear' }] : [],
      };
    }
    if (buildKey && storedBuildKey !== buildKey) {
      return end(started, SESSION_END_REASON.BUILD_CHANGED, [
        { type: 'clear' },
        { type: 'revoke', refreshToken: storedRefreshToken },
      ]);
    }
    return {
      state: booting(started, storedRefreshToken),
      effects: [{ type: 'refresh', refreshToken: storedRefreshToken }],
    };
  },

  /** Sign-in or the SSO hand-off produced tokens. */
  SIGNED_IN(state, { tokens }) {
    return {
      state: signedIn({ ...state, started: true }, tokens),
      effects: [{ type: 'persist', refreshToken: tokens.refreshToken }],
    };
  },

  /** A request found the access token expired. */
  ACCESS_TOKEN_EXPIRED(state) {
    if (state.status !== SIGNED_IN) return same(state);
    return { state, effects: [{ type: 'refresh', refreshToken: state.refreshToken }] };
  },

  /** The refresh for `refreshToken` answered with new tokens. */
  REFRESH_SUCCEEDED(state, { refreshToken, tokens }) {
    if (!hasSession(state) || state.refreshToken !== refreshToken) return same(state);
    return {
      state: signedIn(state, tokens),
      effects:
        tokens.refreshToken === refreshToken
          ? []
          : [{ type: 'persist', refreshToken: tokens.refreshToken }],
    };
  },

  /**
   * The refresh endpoint refused `refreshToken` (401/403). If storage now holds
   * a different token, another tab replaced it while this refresh was in
   * flight: follow that one instead of ending a session it still has.
   */
  REFRESH_REJECTED(state, { refreshToken, storedRefreshToken }) {
    if (!hasSession(state) || state.refreshToken !== refreshToken) return same(state);
    if (storedRefreshToken && storedRefreshToken !== refreshToken) {
      return {
        state: booting(state, storedRefreshToken),
        effects: [{ type: 'refresh', refreshToken: storedRefreshToken }],
      };
    }
    return end(state, SESSION_END_REASON.EXPIRED, [{ type: 'clear' }]);
  },

  /** The refresh did not get an answer about the token (network error, 5xx). */
  REFRESH_FAILED(state, { refreshToken }) {
    if (state.refreshToken !== refreshToken) return same(state);
    if (state.status === BOOTING) return { state: { ...state, status: UNREACHABLE }, effects: [] };
    // Signed in: the request that needed the token fails; the session stays.
    return same(state);
  },

  /** The admin asked to try reaching the server again. */
  RETRY(state) {
    if (state.status !== UNREACHABLE) return same(state);
    return {
      state: booting(state, state.refreshToken),
      effects: [{ type: 'refresh', refreshToken: state.refreshToken }],
    };
  },

  LOGOUT(state) {
    if (!hasSession(state)) return same(state);
    return end(state, SESSION_END_REASON.LOGGED_OUT, [
      { type: 'clear' },
      { type: 'revoke', refreshToken: state.refreshToken },
    ]);
  },

  /**
   * Another tab changed the stored refresh token.
   *
   * - Removed: that tab logged out, so this one does too (it already revoked
   *   and cleared).
   * - A new token while signed in: the other tab renewed the same session and
   *   the token rotated. Adopt it quietly and keep the access token until it
   *   expires. A different admin cannot be behind it, because signing in again
   *   means signing out first, and that removes the key (the case above).
   *   Ending the session here would wipe this tab on every renewal elsewhere,
   *   and two tabs would answer each other's rotation forever.
   *   `REFRESH_REJECTED` follows another tab's token for the same reason.
   * - A new token with no session (signed out, booting, unreachable): that tab
   *   signed in or restored a session, so this one follows it.
   */
  STORAGE_CHANGED(state, { refreshToken }) {
    if (!refreshToken) {
      return hasSession(state) ? end(state, SESSION_END_REASON.OTHER_TAB, []) : same(state);
    }
    if (refreshToken === state.refreshToken) return same(state);
    if (state.status === SIGNED_IN) return { state: { ...state, refreshToken }, effects: [] };
    return {
      state: booting({ ...state, started: true }, refreshToken),
      effects: [{ type: 'refresh', refreshToken }],
    };
  },
};

/**
 * @param {typeof INITIAL_SESSION} state
 * @param {{ type: keyof typeof HANDLERS }} event
 * @returns {{ state: typeof INITIAL_SESSION, effects: Array<Object> }}
 */
export const transition = (state, event) => {
  const handler = HANDLERS[event.type];
  if (!handler) throw new Error(`Unknown session event: ${event.type}`);
  return handler(state, event);
};

/** Whether `state` holds an access token that is still good at `now`. */
export const hasFreshAccessToken = (state, now) =>
  state.status === SIGNED_IN && !!state.accessToken && now < state.accessTokenExpiresAt;
